// QXAI ULTIMATE API SERVICE
// Version: 3.0.1-FIXED
// Privacy-First Hash Authentication + Advanced Features
// Backend: https://vtaas-backend-220419304601.us-central1.run.app
// 
// ✨ FIXES:
// - Changed /api/router to /api/qxai/chat (QXAI uses /api/qxai/chat, NOT /api/router)
// - /api/router is for GemIQ, not QXAI
// - All text chat now uses correct endpoint

import { getAuth } from "firebase/auth";
import CryptoJS from 'crypto-js';

const VTAAS_API_BASE = 'https://vtaas-backend-220419304601.us-central1.run.app';

// ============================================================================
// CLEAN RESPONSE - Remove special tokens from model output
// ============================================================================

/**
 * Clean special tokens from model output
 * These tokens are used internally by LLMs but shouldn't be visible to users
 */
const cleanResponse = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Remove all common special tokens
  let cleaned = text
    // Llama/Mistral tokens
    .replace(/<s>/gi, '')
    .replace(/<\/s>/gi, '')
    .replace(/\[\/s\]/gi, '')
    .replace(/\[s\]/gi, '')
    
    // GPT tokens
    .replace(/<\|endoftext\|>/gi, '')
    .replace(/<\|startoftext\|>/gi, '')
    .replace(/<\|im_start\|>/gi, '')
    .replace(/<\|im_end\|>/gi, '')
    
    // Llama system tokens
    .replace(/<<SYS>>/gi, '')
    .replace(/<\/SYS>>/gi, '')
    .replace(/\[INST\]/gi, '')
    .replace(/\[\/INST\]/gi, '')
    
    // Other common tokens
    .replace(/<\|user\|>/gi, '')
    .replace(/<\|assistant\|>/gi, '')
    .replace(/<\|system\|>/gi, '')
    .replace(/\[USER\]/gi, '')
    .replace(/\[ASSISTANT\]/gi, '')
    .replace(/\[SYSTEM\]/gi, '');
  
  // Remove extra whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  return cleaned;
};

// ============================================================================
// CACHING SYSTEM (NEW!)
// ============================================================================

const chatCache = new Map();
const CHAT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const creditsCache = {
    value: null,
    timestamp: 0,
    duration: 30 * 1000 // 30 seconds
};

const clearCache = (key) => {
    if (key) {
        chatCache.delete(key);
    } else {
        chatCache.clear();
        creditsCache.value = null;
        creditsCache.timestamp = 0;
    }
};

// ============================================================================
// ABORT MANAGER (NEW!)
// ============================================================================

class AbortManager {
    constructor() {
        this.controllers = new Map();
    }

    create(key) {
        // Abort any existing request with this key
        this.abort(key);
        
        // Create new controller
        const controller = new AbortController();
        this.controllers.set(key, controller);
        return controller;
    }

    abort(key) {
        const controller = this.controllers.get(key);
        if (controller) {
            controller.abort();
            this.controllers.delete(key);
        }
    }

    getSignal(key) {
        const controller = this.controllers.get(key);
        return controller ? controller.signal : undefined;
    }

    clear() {
        this.controllers.forEach(controller => controller.abort());
        this.controllers.clear();
    }
}

const abortManager = new AbortManager();

// ============================================================================
// DEBOUNCING (NEW!)
// ============================================================================

const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        return new Promise((resolve, reject) => {
            timeoutId = setTimeout(async () => {
                try {
                    const result = await func(...args);
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            }, delay);
        });
    };
};

// ============================================================================
// HASH AUTHENTICATION (Privacy-First)
// ============================================================================

/**
 * Generate SHA-256 hash from email
 * This ensures backend NEVER sees actual email
 */
const generateHash = (email) => {
    if (!email) throw new Error('Email is required to generate hash');
    return CryptoJS.SHA256(email.toLowerCase().trim()).toString();
};

/**
 * Get user hash - QXAI's authentication method
 * Cached in localStorage for performance
 */
const getUserHash = () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
        throw new Error('User not authenticated');
    }
    
    // Check localStorage first (performance optimization)
    let userHash = localStorage.getItem('qxai_user_hash');
    
    // If not cached, generate and store
    if (!userHash) {
        userHash = generateHash(currentUser.email);
        localStorage.setItem('qxai_user_hash', userHash);
    }
    
    return userHash;
};

/**
 * Get a Firebase ID token for the signed-in user, or null when unavailable.
 * The backend accepts Authorization: Bearer <token> alongside X-User-Hash;
 * with QXAI_CHAT_REQUIRE_VERIFIED enabled it requires it on chat.
 */
const getIdTokenSafe = async () => {
    try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) return null;
        return await currentUser.getIdToken();
    } catch (e) {
        console.warn('QXAI: could not obtain ID token', e);
        return null;
    }
};

/**
 * Clear user hash on logout
 */
export const clearUserHash = () => {
    localStorage.removeItem('qxai_user_hash');
    clearCache(); // Also clear all caches
    abortManager.clear(); // Abort all requests
};

// ============================================================================
// MODEL CONFIGURATION
// ============================================================================

// Free models that don't consume credits
export const FREE_MODELS = [
    // Gemini Models (via VTaaS provisioning)
    'gemini-2.5-pro',
    'gemini-2.5-flash',
    
    // OpenRouter Free Models
    'gemini-2.0-flash-experimental',
    'qwen3-235b',
    'deepseek-v3.1',
    'sonoma-dusk-alpha',
    'qwen3-coder-480b',
    'gemma-3n-2b',
    'gpt-oss-120b',
    
    // Free Image Models
    'flux-schnell',
    'sdxl-turbo',
    'sd3',
    'qwen',
    'black-forest-labs/FLUX-1-schnell:free',
    'stabilityai/sdxl-turbo:free',
    'HiDream-ai/HiDream-I1-Full:free',
    'lodestones/Chroma:free',
    'ByteDance/InfiniteYou:free'
];

// Model mapping for frontend -> backend conversion
export const MODEL_MAPPING = {
    // CLI MODELS (Gemini via VTaaS provisioning)
    'Gemini': 'gemini-2.5-pro',
    'gemini-2.5-pro': 'gemini-2.5-pro',
    'gemini-2.5-flash': 'gemini-2.5-flash',
    'gemini-1.5-pro': 'gemini-2.5-pro',
    'gemini-1.5-flash': 'gemini-2.5-flash',
    'smart-router-auto': 'gemini-2.5-pro',
    
    // OPENROUTER FREE MODELS
    'gemini-2.0-flash-experimental': 'gemini-2.0-flash-experimental',
    'qwen3-235b': 'qwen3-235b',
    'qwen3-coder-480b': 'qwen3-coder-480b',
    'deepseek-v3.1': 'deepseek-v3.1',
    'sonoma-dusk-alpha': 'sonoma-dusk-alpha',
    'gemma-3n-2b': 'gemma-3n-2b',
    'gpt-oss-120b': 'gpt-oss-120b',
    
    // FREE IMAGE MODELS
    'FLUX-Free': 'flux-schnell',
    'SDXL-Free': 'sdxl-turbo',
    'SD3-Free': 'sd3',
    'Qwen-Free': 'qwen',
    'HiDream-Free': 'HiDream-ai/HiDream-I1-Full:free',
    'Chroma-Free': 'lodestones/Chroma:free',
    'InfiniteYou-Free': 'ByteDance/InfiniteYou:free',
    
    // PREMIUM TEXT MODELS
    'gpt-4': 'gpt-4o',
    'gpt-4o': 'gpt-4o',
    'GPT-4': 'gpt-4o',
    'claude-3.5-sonnet': 'anthropic/claude-3.5-sonnet',
    'Claude': 'anthropic/claude-3.5-sonnet',
    
    // PREMIUM IMAGE MODELS
    'minimax-image': 'minimax/image-01',
    'photon-flash': 'luma/photon-flash',
    'imagen-4-fast': 'google/imagen-4-fast',
    'imagen-3-fast': 'google/imagen-3-fast',
    'recraft-v3': 'recraft-ai/recraft-v3',
    'imagen-3': 'google/imagen-3',
    'seedream': 'bytedance/seedream-3',
    'photon': 'luma/photon',
    'flux-pro': 'black-forest-labs/FLUX-1.1-pro',
    'imagen-4': 'google/imagen-4',
    'sd3.5': 'stabilityai/sd3.5',
    'imagen-4-ultra': 'google/imagen-4-ultra',
    'flux-ultra': 'black-forest-labs/flux-1.1-pro-ultra',
    
    // PREMIUM VIDEO MODELS
    'seedance-lite': 'bytedance/seedance-1-lite',
    'kling-standard': 'kwaivgi/kling-2.1-standard',
    'hailuo-standard': 'minimax/hailuo-02-standard',
    'kling-pro': 'kwaivgi/kling-2.1-pro',
    'seedance-pro': 'bytedance/seedance-1-pro',
    'veo-2': 'google/veo-2',
    'kling-master': 'kwaivgi/kling-2.1-master',
    'veo-3-fast': 'google/veo-3-fast',
    'veo-3': 'google/veo-3'
};

// Credit costs
export const CREDIT_COSTS = {
    // Free models
    'gemini-2.5-pro': 0,
    'gemini-2.5-flash': 0,
    'gemini-2.0-flash-experimental': 0,
    'qwen3-235b': 0,
    'deepseek-v3.1': 0,
    'flux-schnell': 0,
    'sdxl-turbo': 0,
    'sd3': 0,
    'qwen': 0,
    
    // Premium text models
    'gpt-4o': 3,
    'anthropic/claude-3.5-sonnet': 3,
    
    // Premium image models
    'minimax/image-01': 1,
    'luma/photon-flash': 1,
    'google/imagen-4-fast': 2,
    'google/imagen-3-fast': 2,
    'recraft-ai/recraft-v3': 3,
    'google/imagen-3': 3,
    'bytedance/seedream-3': 3,
    'luma/photon': 3,
    'black-forest-labs/FLUX-1.1-pro': 4,
    'google/imagen-4': 4,
    'stabilityai/sd3.5': 5,
    'google/imagen-4-ultra': 6,
    'black-forest-labs/flux-1.1-pro-ultra': 6,
    
    // Premium video models
    'bytedance/seedance-1-lite': 15,
    'kwaivgi/kling-2.1-standard': 18,
    'minimax/hailuo-02-standard': 23,
    'kwaivgi/kling-2.1-pro': 32,
    'bytedance/seedance-1-pro': 38,
    'google/veo-2': 30,
    'kwaivgi/kling-2.1-master': 92,
    'google/veo-3-fast': 32,
    'google/veo-3': 65
};

// Check if model is free
export const isModelFree = (modelId) => {
    const backendModel = MODEL_MAPPING[modelId] || modelId;
    return FREE_MODELS.includes(backendModel) || 
           FREE_MODELS.includes(modelId) ||
           CREDIT_COSTS[backendModel] === 0;
};

// Get model credit cost
export const getModelCost = (modelId) => {
    const backendModel = MODEL_MAPPING[modelId] || modelId;
    return CREDIT_COSTS[backendModel] || 1;
};

// Model categories
const CLI_MODELS = ['Gemini', 'gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-1.5-pro', 'gemini-1.5-flash'];
const OPENROUTER_MODELS = ['gemini-2.0-flash-experimental', 'qwen3-235b', 'deepseek-v3.1', 'sonoma-dusk-alpha', 'qwen3-coder-480b', 'gemma-3n-2b', 'gpt-oss-120b'];

// ============================================================================
// CORE AI FUNCTIONS - FIXED!
// ============================================================================

/**
 * Main chat function - QXAI version
 * WITH CACHING AND ABORT SUPPORT
 * 
 * 🔧 FIXED: Changed all /api/router to /api/qxai/chat
 * QXAI uses /api/qxai/chat for ALL text generation
 * /api/router is for GemIQ, not QXAI!
 */
export const askAi = async (text, userPlan = 'free', selectedModel = 'Gemini', options = {}) => {
    try {
        const userHash = getUserHash();
        const backendModel = MODEL_MAPPING[selectedModel] || selectedModel;
        const isFreeModel = isModelFree(selectedModel);
        
        // Create cache key
        const cacheKey = `${selectedModel}:${text}`;
        
        // Check cache first (unless options.skipCache)
        if (!options.skipCache) {
            const cached = chatCache.get(cacheKey);
            if (cached && (Date.now() - cached.timestamp < CHAT_CACHE_DURATION)) {
                return cached.data;
            }
        }
        
        // Create abort controller for this request
        const abortKey = `chat:${Date.now()}`;
        const controller = abortManager.create(abortKey);
        
        // 🔧 FIX: All QXAI text chat uses /api/qxai/chat
        // The backend will handle routing to free or paid models
        const endpoint = '/api/qxai/chat';
        const requestBody = {
            message: text,
            model: backendModel
        };
        
        const idToken = await getIdTokenSafe();
        const response = await fetch(`${VTAAS_API_BASE}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
                ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}),
            },
            body: JSON.stringify(requestBody),
            signal: controller.signal
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (response.status === 402) {
                throw new Error(`Insufficient credits. This model requires ${errorData.credits_required || getModelCost(selectedModel)} credits.`);
            }
            throw new Error(errorData.error || `Request failed: ${response.status}`);
        }
        
        const result = await response.json();
        
        const formattedResult = {
            // Apply cleanResponse to remove special tokens ✅
            response: cleanResponse(result.response || result.content || result.message),
            choices: result.choices || [{
                message: {
                    // Also clean the choices format ✅
                    content: cleanResponse(result.response || result.content || result.message)
                }
            }],
            model: result.model || backendModel,
            credits_used: result.credits_used || 0,
            credits_remaining: result.credits_remaining,
            is_free_model: result.is_free_model || isFreeModel,
            provider: result.provider || 'qxai',
            source: result.source || 'qxai-chat'
        };
        
        // Cache the result
        chatCache.set(cacheKey, {
            data: formattedResult,
            timestamp: Date.now()
        });
        
        // Clean up abort controller
        abortManager.abort(abortKey);
        
        return formattedResult;
        
    } catch (error) {
        if (error.name === 'AbortError') {
            throw new Error('Request cancelled');
        }
        console.error('QXAI askAi error:', error);
        throw error;
    }
};

/**
 * Simple chat function
 */
export const simpleChat = async (message, model = 'gemini-2.5-pro') => {
    return askAi(message, 'free', model);
};

// ============================================================================
// MEDIA GENERATION FUNCTIONS
// ============================================================================

/**
 * Normalize media response from backend
 * Backend returns camelCase (generatedImage, generatedVideo)
 * Frontend expects snake_case (image_url, video_url)
 */
const normalizeMediaResponse = (response, mediaType = 'image') => {
    if (!response) return response;
    
    const normalized = { ...response };
    
    // Normalize image URL
    if (mediaType === 'image' && response.generatedImage) {
        normalized.image_url = response.generatedImage;
    }
    
    // Normalize video URL
    if (mediaType === 'video' && response.generatedVideo) {
        normalized.video_url = response.generatedVideo;
    }
    
    // Normalize credits (camelCase to snake_case)
    if (response.creditsUsed !== undefined) {
        normalized.credits_used = response.creditsUsed;
    }
    if (response.creditsRemaining !== undefined) {
        normalized.credits_remaining = response.creditsRemaining;
    }
    
    return normalized;
};

/**
 * Generate image
 */
export const generateImage = async (params) => {
    try {
        const userHash = getUserHash();
        const model = MODEL_MAPPING[params.model] || params.model;
        
        const response = await fetch(`${VTAAS_API_BASE}/api/qxai/image/generate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify({
                prompt: params.prompt,
                model: model,
                width: params.width || 1024,
                height: params.height || 1024,
                aspect_ratio: params.aspectRatio,
                num_images: params.numImages || 1
            })
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (response.status === 402) {
                throw new Error(`Insufficient credits. Image generation requires ${getModelCost(params.model)} credits.`);
            }
            throw new Error(errorData.error || 'Image generation failed');
        }
        
        const rawResponse = await response.json();
        return normalizeMediaResponse(rawResponse, 'image');
    } catch (error) {
        console.error('QXAI generateImage error:', error);
        throw error;
    }
};

/**
 * Generate video
 */
export const generateVideo = async (params) => {
    try {
        const userHash = getUserHash();
        const model = MODEL_MAPPING[params.model] || params.model;
        
        const response = await fetch(`${VTAAS_API_BASE}/api/qxai/video/generate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify({
                prompt: params.prompt,
                model: model,
                duration: params.duration || 5,
                aspect_ratio: params.aspectRatio
            })
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (response.status === 402) {
                throw new Error(`Insufficient credits. Video generation requires ${getModelCost(params.model)} credits.`);
            }
            throw new Error(errorData.error || 'Video generation failed');
        }
        
        const rawResponse = await response.json();
        return normalizeMediaResponse(rawResponse, 'video');
    } catch (error) {
        console.error('QXAI generateVideo error:', error);
        throw error;
    }
};

/**
 * Generate audio (TTS)
 */
export const generateAudio = async (text, voice = 'alloy', model = 'tts-1') => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/audio/generate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify({
                text,
                voice,
                model
            })
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Audio generation failed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI generateAudio error:', error);
        throw error;
    }
};

/**
 * Transcribe audio to text
 */
export const transcribeAudio = async (audioFile, model = 'whisper-1') => {
    try {
        const userHash = getUserHash();
        const formData = new FormData();
        formData.append('audio', audioFile);
        formData.append('model', model);
        
        const response = await fetch(`${VTAAS_API_BASE}/api/audio/transcribe`, {
            method: 'POST',
            headers: {
                'X-User-Hash': userHash,
            },
            body: formData
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Audio transcription failed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI transcribeAudio error:', error);
        throw error;
    }
};

// ============================================================================
// AI TOOLS
// ============================================================================

/**
 * Remove background from image
 */
export const removeBackground = async (imageUrl) => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/tools/remove-bg`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify({ image_url: imageUrl })
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Background removal failed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI removeBackground error:', error);
        throw error;
    }
};

/**
 * FLUX image editing
 */
export const fluxEdit = async (params) => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/tools/flux-edit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify(params)
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'FLUX edit failed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI fluxEdit error:', error);
        throw error;
    }
};

/**
 * Qwen image editing
 */
export const qwenEdit = async (params) => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/tools/qwen-edit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify(params)
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Qwen edit failed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI qwenEdit error:', error);
        throw error;
    }
};

/**
 * Scrape website
 */
export const scrapeWebsite = async (url) => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/tools/scrape`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify({ url })
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Website scraping failed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI scrapeWebsite error:', error);
        throw error;
    }
};

/**
 * Humanize AI text
 */
export const humanizeText = async (text) => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/tools/humanize`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify({ text })
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Text humanization failed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI humanizeText error:', error);
        throw error;
    }
};

// ============================================================================
// TASK MANAGEMENT
// ============================================================================

/**
 * Create new task
 */
export const createTask = async (taskData) => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/tasks`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify(taskData)
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Task creation failed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI createTask error:', error);
        throw error;
    }
};

/**
 * Get user's tasks
 */
export const getTasks = async (filters = {}) => {
    try {
        const userHash = getUserHash();
        const queryParams = new URLSearchParams(filters).toString();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/tasks?${queryParams}`, {
            method: 'GET',
            headers: {
                'X-User-Hash': userHash,
            }
        });
        
        if (!response.ok) {
            throw new Error('Failed to get tasks');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI getTasks error:', error);
        throw error;
    }
};

/**
 * Update task
 */
export const updateTask = async (taskId, updates) => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/tasks/${taskId}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify(updates)
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Task update failed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI updateTask error:', error);
        throw error;
    }
};

/**
 * Delete task
 */
export const deleteTask = async (taskId) => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/tasks/${taskId}`, {
            method: 'DELETE',
            headers: {
                'X-User-Hash': userHash,
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Task deletion failed');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI deleteTask error:', error);
        throw error;
    }
};

// ============================================================================
// ACCOUNT MANAGEMENT
// ============================================================================

/**
 * Get user credits
 * WITH CACHING
 */
export const getUserCredits = async () => {
    try {
        // Check cache first
        if (creditsCache.value !== null && 
            (Date.now() - creditsCache.timestamp < creditsCache.duration)) {
            return creditsCache.value;
        }
        
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/user/credits`, {
            method: 'GET',
            headers: {
                'X-User-Hash': userHash,
            }
        });
        
        if (!response.ok) {
            throw new Error('Failed to get credits');
        }
        
        const data = await response.json();
        const credits = data.credits || 0;
        
        // Cache the result in consistent format
        const result = { success: true, credits: credits || 0 };
        creditsCache.value = result;
        creditsCache.timestamp = Date.now();
        
        return result;
    } catch (error) {
        console.error('QXAI getUserCredits error:', error);
        // Return fallback instead of throwing
        return {
            success: true,
            credits: 0,
            tier: 'None',
            credits_used_today: 0
        };
    }
};

/**
 * Get user info
 */
export const getUserInfo = async () => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/user/info`, {
            method: 'GET',
            headers: {
                'X-User-Hash': userHash,
            }
        });
        
        if (!response.ok) {
            throw new Error('Failed to get user info');
        }
        
        return await response.json();
    } catch (error) {
        console.error('QXAI getUserInfo error:', error);
        throw error;
    }
};

/**
 * Get video usage stats
 */
export const getVideoUsageStats = async () => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/qxai/user/video-usage`, {
            method: 'GET',
            headers: {
                'X-User-Hash': userHash,
            }
        });
        
        if (!response.ok) {
            throw new Error('Failed to get video usage stats');
        }
        
        const data = await response.json();
        
        return {
            success: true,
            usage_stats: {
                video_allowances: {
                    daily: {
                        used: data.daily?.used || 0,
                        limit: data.daily?.limit || 3
                    },
                    monthly: {
                        used: data.monthly?.used || 0,
                        limit: data.monthly?.limit || 15
                    }
                }
            }
        };
    } catch (error) {
        console.error('QXAI getVideoUsageStats error:', error);
        return {
            success: true,
            usage_stats: {
                video_allowances: {
                    daily: { used: 0, limit: 3 },
                    monthly: { used: 0, limit: 15 }
                }
            }
        };
    }
};

/**
 * Upgrade plan
 */
export const upgradePlan = async (newPlan) => {
    try {
        const userHash = getUserHash();
        
        const response = await fetch(`${VTAAS_API_BASE}/api/subscription/upgrade`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Hash': userHash,
            },
            body: JSON.stringify({ plan: newPlan })
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Upgrade failed');
        }
        
        // Clear credits cache after upgrade
        creditsCache.value = null;
        creditsCache.timestamp = 0;
        
        return await response.json();
    } catch (error) {
        console.error('QXAI upgradePlan error:', error);
        throw error;
    }
};

// Old purchaseCredits function removed - now using Stripe integration below

// ============================================================================
// HEALTH & UTILITY
// ============================================================================

/**
 * Health check
 */
export const healthCheck = async () => {
    try {
        const response = await fetch(`${VTAAS_API_BASE}/health`, {
            method: 'GET'
        });
        
        return await response.json();
    } catch (error) {
        console.error('Health check error:', error);
        throw error;
    }
};

/**
 * Get available models by category
 */
export const getModelsByCategory = () => {
    return {
        text: {
            name: "Text Generation",
            description: "AI chat models",
            models: [
                { id: "Gemini", name: "Gemini 2.5 Pro", cost: "Free", default: true },
                { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash", cost: "Free" },
                { id: "gemini-2.0-flash-experimental", name: "Gemini 2.0 Flash (Exp)", cost: "Free" },
                { id: "qwen3-235b", name: "Qwen 3 235B", cost: "Free" },
                { id: "deepseek-v3.1", name: "DeepSeek V3.1", cost: "Free" },
                { id: "gpt-4o", name: "GPT-4o", cost: "3 credits" },
                { id: "claude-3.5-sonnet", name: "Claude 3.5", cost: "3 credits" }
            ]
        },
        image: {
            name: "Image Generation",
            description: "Create stunning images",
            models: [
                { id: "FLUX-Free", name: "FLUX Schnell", cost: "Free", default: true },
                { id: "SDXL-Free", name: "SDXL Turbo", cost: "Free" },
                { id: "imagen-4-fast", name: "Imagen 4 Fast", cost: "2 credits" },
                { id: "imagen-4", name: "Imagen 4", cost: "4 credits" },
                { id: "flux-pro", name: "FLUX Pro", cost: "4 credits" }
            ]
        },
        video: {
            name: "Video Generation",
            description: "Generate AI videos",
            models: [
                { id: "kling-standard", name: "Kling Standard", cost: "18 credits" },
                { id: "veo-2", name: "VEO 2", cost: "30 credits" },
                { id: "veo-3", name: "VEO 3", cost: "65 credits" }
            ]
        }
    };
};

// ============================================================================
// CACHE & ABORT UTILITIES
// ============================================================================

/**
 * Clear all caches
 */
export const clearAllCaches = () => {
    clearCache();
};

/**
 * Cancel all in-flight requests
 */
export const cancelAllRequests = () => {
    abortManager.clear();
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
    // Core AI
    askAi,
    simpleChat,
    
    // Media generation
    generateImage,
    generateVideo,
    generateAudio,
    transcribeAudio,
    
    // AI tools
    removeBackground,
    fluxEdit,
    qwenEdit,
    scrapeWebsite,
    humanizeText,
    
    // Task management
    createTask,
    getTasks,
    updateTask,
    deleteTask,
    
    // Account management
    getUserCredits,
    getUserInfo,
    getVideoUsageStats,
    upgradePlan,
    
    // Auth utilities
    getUserHash,
    clearUserHash,
    generateHash,
    
    // Cache & abort utilities
    clearAllCaches,
    cancelAllRequests,
    
    // Response cleaning
    cleanResponse,
    
    // Health & utility
    healthCheck,
    getModelsByCategory,
    
    // Constants
    FREE_MODELS,
    MODEL_MAPPING,
    CREDIT_COSTS,
    isModelFree,
    getModelCost
};


// ============================================================================
// STRIPE BILLING & SUBSCRIPTION METHODS
// ============================================================================

/**
 * Create Stripe Checkout Session for subscription
 * @param {string} userHash - User's hash ID
 * @param {string} priceId - Stripe price ID (e.g., 'price_qxai_starter_monthly')
 * @param {string} type - 'subscription' or 'credits'
 * @returns {Promise<{sessionUrl: string}>}
 */
export const createCheckoutSession = async (userHash, priceId, type = 'subscription') => {
  try {
    const response = await fetch(`${VTAAS_API_BASE}/api/stripe/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Hash': userHash,
        'X-Platform': 'qxai'
      },
      body: JSON.stringify({
        priceId,
        type,
        successUrl: `${window.location.origin}/success`,
        cancelUrl: `${window.location.origin}/`
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create checkout session');
    }

    return await response.json();
  } catch (error) {
    console.error('Create checkout session error:', error);
    throw error;
  }
};

/**
 * Purchase credit package
 * @param {string} userHash - User's hash ID
 * @param {string} priceId - Stripe price ID for credit package
 * @param {number} credits - Number of credits in package
 * @returns {Promise<{sessionUrl: string}>}
 */
export const purchaseCredits = async (userHash, priceId, credits) => {
  try {
    const response = await fetch(`${VTAAS_API_BASE}/api/stripe/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Hash': userHash,
        'X-Platform': 'qxai'
      },
      body: JSON.stringify({
        priceId,
        type: 'payment',
        successUrl: `${window.location.origin}/success`,
        cancelUrl: `${window.location.origin}/`
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('Error purchasing credits:', error);
    throw error;
  }
};

/**
 * Get user's subscription status
 * @param {string} userHash - User's hash ID
 * @returns {Promise<{tier: string, status: string, creditsRemaining: number}>}
 */
export const getSubscriptionStatus = async (userHash) => {
  try {
    const response = await fetch(`${VTAAS_API_BASE}/api/stripe/subscription-status`, {
      method: 'GET',
      headers: {
        'X-User-Hash': userHash,
        'X-Platform': 'qxai'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get subscription status');
    }

    return await response.json();
  } catch (error) {
    console.error('Get subscription status error:', error);
    throw error;
  }
};

/**
 * Cancel subscription
 * @param {string} userHash - User's hash ID
 * @returns {Promise<{success: boolean}>}
 */
export const cancelSubscription = async (userHash) => {
  try {
    const response = await fetch(`${VTAAS_API_BASE}/api/stripe/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Hash': userHash,
        'X-Platform': 'qxai'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to cancel subscription');
    }

    return await response.json();
  } catch (error) {
    console.error('Cancel subscription error:', error);
    throw error;
  }
};

/**
 * Get billing history
 * @param {string} userHash - User's hash ID
 * @returns {Promise<Array>}
 */
export const getBillingHistory = async (userHash) => {
  try {
    const response = await fetch(`${VTAAS_API_BASE}/api/stripe/billing-history`, {
      method: 'GET',
      headers: {
        'X-User-Hash': userHash,
        'X-Platform': 'qxai'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get billing history');
    }

    return await response.json();
  } catch (error) {
    console.error('Get billing history error:', error);
    throw error;
  }
};
